import { toggleDeveloperMode, useDeveloperModeEnabled } from '@chipinside/devmode'
import { Translate } from '@chipinside/translate'
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  Switch,
  Typography,
  TypographyProps,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { Bell, ChevronRight, Code2, ListIcon, Lock, Plug, Settings, User } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import Avatar from '#/components/Avatar'
import { Drawer } from '#/components/Drawer'
import { PerfilIcon } from '#/components/Icon/Collection'
import { login, useUserInfo } from '#/store/ducks/auth'
import { useFarmSlug } from '#/store/ducks/farm'
import { usePermissions } from '#/store/ducks/permissions'
import { UserInfoType } from '#/store/hocs/withUser'
import { useFormBuilder } from '#/store/hooks/useFormBuilder'
import Colors from '#/styles/Colors'
import Palette from '#/styles/Palette'
import { isLocal } from '#/utils'
import { getInitials } from '#/utils/getInitials'
import { getLink } from '#/utils/getLink'

const CenteredHeaderContent = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  borderBottom: `1px dashed ${Palette.gray.self[200]}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginBottom: 20,
  paddingBottom: 40,
  [theme.breakpoints.down('sm')]: {
    marginBottom: 10,
    paddingBottom: 20,
  },
  width: '100%',
}))

type RowLabelProps = {
  icon: ReactNode
  title: string
  subtitle?: string
  color?: TypographyProps['color']
}

function RowLabel({ color = 'grey.600', icon, subtitle, title }: RowLabelProps) {
  return (
    <>
      <Avatar size="small">{icon}</Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" color={color} sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </>
  )
}

type MenuItem = {
  enabled: () => boolean
  right?: ReactNode
  icon: ReactNode
  title: string
  subtitle?: string
  to?: string
  external?: false
  onClick?: () => void
  color?: TypographyProps['color']
}

type MenuCategory = {
  label: string
  items: MenuItem[]
}

function getComponentProps(item: MenuItem): Record<string, unknown> {
  if (!item.to) {
    return { onClick: item.onClick }
  }

  return item.external
    ? {
        component: 'a',
        href: item.to,
        onClick: Drawer.close,
        rel: 'noopener noreferrer',
        target: '_blank',
      }
    : {
        component: Link,
        onClick: Drawer.close,
        to: item.to,
      }
}

function MenuItemRow({ item }: { item: MenuItem }) {
  const componentProps = getComponentProps(item)

  return (
    <ListItemButton {...(componentProps as any)} sx={{ borderRadius: 2, gap: 1.5, my: 0.25, p: 1 }}>
      <RowLabel icon={item.icon} title={item.title} subtitle={item.subtitle} color={item.color} />
      {item?.right ?? <ChevronRight size={16} style={{ color: Colors.muted, flexShrink: 0 }} />}
    </ListItemButton>
  )
}

function UserMenuList({ categories }: { categories: MenuCategory[] }) {
  const visibleCategories = categories
    .map(category => ({
      ...category,
      items: category.items.filter(item => item.enabled() && Boolean(item.to || item.onClick)),
    }))
    .filter(category => category.items.length > 0)

  return (
    <List disablePadding>
      {visibleCategories.map(category => (
        <Box key={category.label}>
          <Typography variant="overline" sx={{ color: 'grey.600', fontWeight: 500 }}>
            {category.label}
          </Typography>
          {category.items.map((item, index) => (
            <MenuItemRow key={`${category.label}-${index}`} item={item} />
          ))}
        </Box>
      ))}
    </List>
  )
}

function UserMenuContent() {
  const farmSlug = useFarmSlug()

  const { email, farm_count, has_company, name } = useUserInfo() as UserInfoType
  const permissions = usePermissions()
  const developerModeEnabled = useDeveloperModeEnabled()
  const { build } = useFormBuilder()

  const categories: MenuCategory[] = [
    {
      items: [
        {
          enabled: () => true,
          icon: <User size={18} />,
          subtitle: Translate({ messageKey: 'edit_personal_info' }),
          title: Translate({ messageKey: 'user_informations' }),
          to: getLink({ id: farmSlug ? 'farm-user-info' : 'system-user-info' }),
        },
        {
          enabled: () => true,
          icon: <Bell size={18} />,
          subtitle: Translate({ messageKey: 'notification_settings_description' }),
          title: Translate({ messageKey: 'notification_plural' }),
          to: getLink({ id: farmSlug ? 'farm-user-notifications' : 'system-user-notifications' }),
        },
        {
          enabled: () => true,
          icon: <Lock size={18} />,
          onClick: () => {
            Drawer.close()
            build({ formName: 'user', formType: 'password' })
          },
          subtitle: Translate({ messageKey: 'manage_password_and_access' }),
          title: Translate({ messageKey: 'change_password' }),
        },
      ],
      label: Translate({ messageKey: 'user_environment' }),
    },
    {
      items: [
        {
          enabled: () => Boolean(farmSlug) && permissions.farm.view_config_farm,
          icon: <Settings size={18} />,
          subtitle: Translate({ messageKey: 'farm_settings_description' }),
          title: Translate({ messageKey: 'farm_settings' }),
          to: getLink({ id: 'farm-settings-overview' }),
        },
        {
          enabled: () =>
            Boolean(farmSlug) &&
            (permissions.farm.list_config_farm_integrations ||
              permissions.system.manage_farm_token),
          icon: <Plug size={18} />,
          subtitle: Translate({ messageKey: 'integrations_description' }),
          title: Translate({ messageKey: 'integrations' }),
          to: getLink({ id: 'farm-integrations-available' }),
        },
      ],
      label: Translate({ messageKey: 'farm' }),
    },
    {
      items: [
        {
          color: 'primary',
          enabled: () => Boolean(farmSlug) && (has_company || farm_count > 0),
          icon: <ListIcon size={18} />,
          subtitle: Translate({ messageKey: 'farm_list_description' }),
          title: Translate({ messageKey: 'farm_list' }),
          to: getLink({ id: 'system-accesses' }),
        },
      ],
      label: Translate({ messageKey: 'accesses' }),
    },
    {
      items: [
        {
          enabled: () => isLocal,
          icon: <Code2 size={18} />,
          onClick: toggleDeveloperMode,
          right: <Switch size="small" checked={developerModeEnabled} color="primary" />,
          subtitle: Translate({ messageKey: 'developer_mode_description' }),
          title: Translate({ messageKey: 'developer_mode' }),
        },
      ],
      label: Translate({ messageKey: 'developer_mode' }),
    },
  ]

  return (
    <>
      <CenteredHeaderContent>
        <Avatar
          variant="circular"
          sx={{ bgcolor: 'primary.main', color: '#fff', fontSize: 32, height: 80, width: 80 }}
        >
          {getInitials(name, 1)}
        </Avatar>
        <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {email}
          </Typography>
        </Box>
      </CenteredHeaderContent>
      <UserMenuList categories={categories} />
    </>
  )
}

export default function UserMenu() {
  const { name } = useUserInfo() as UserInfoType
  const dispatch = useDispatch()
  const [disconnect, setDisconnect] = useState(false)

  useEffect(() => {
    if (!name && !disconnect) {
      dispatch(login())
    }
  }, [disconnect, dispatch, name])

  const handleOpen = () => {
    Drawer.open({
      content: <UserMenuContent />,
      footer: (
        <Button
          component={Link}
          to="/logout"
          onClick={() => {
            setDisconnect(true)
            Drawer.close()
          }}
          variant="soft"
          color="error"
          fullWidth
        >
          {Translate({ messageKey: 'sign_out_of_account' })}
        </Button>
      ),
      header: { sx: { paddingBottom: { sm: '20px', xs: 0 } } },
    })
  }

  if (!name) return null

  return (
    <IconButton onClick={handleOpen} size="large">
      <PerfilIcon />
    </IconButton>
  )
}
